import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet";
import { ArrowRight, BadgeCheck, FileText, Rocket, ShieldCheck, Bike, GaugeCircle } from 'lucide-react';
import { motion } from 'framer-motion';
import {
    FaShieldAlt,        // For Zero Depreciation
    FaTools,            // For Battery Protection
    FaTruckPickup,      // For Roadside Assistance
    FaOilCan,           // For Consumables Cover
    FaFileInvoice,      // For Return to Invoice
    FaCloudSunRain,
    FaUserLock,
    FaCarCrash,
    FaUserShield,
} from "react-icons/fa";
import { GiScooter } from "react-icons/gi";
import FAQSchema from "../../FAQSchema";
import AdBanner from "../AdPage/AdBanner";

function Icon({ id, open }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 24 24"
      strokeWidth={2}
      stroke="currentColor"
      className={`${id === open ? "rotate-180" : ""} h-5 w-5 transition-transform`}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M19.5 8.25l-7.5 7.5-7.5-7.5"
      />
    </svg>
  );
}

const OlaElectricScooterInsurance = () => {
  const [activeIndex, setActiveIndex] = useState(0);
  const [insuranceData, setInsuranceData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const faqs = [
    {
      question: "Is insurance mandatory for OLA scooters?",
      answer: "Yes, at least third-party insurance is required under Indian law.",
    },
    {
      question: "What type of insurance should I buy?",
      answer: "Comprehensive plans with EV-specific add-ons are ideal for OLA scooters.",
    },
    {
      question: "How much does OLA insurance cost?",
      answer: "Premiums start from ₹1,000 and can go up to ₹1,800 depending on the model and cover.",
    },
    {
      question: "Is battery damage covered in standard policies?",
      answer: "Usually not. You need a battery protection add-on for coverage.",
    },
    {
      question: "Can I insure the charger that came with the scooter?",
      answer: "Yes, through a charger/accessory protection add-on.",
    },
    {
      question: "Does OLA offer in-house insurance?",
      answer: "Yes, OLA often offers its own insurance during purchase, but you can choose third-party insurers too.",
    },
    {
      question: "What happens if I skip renewing the policy on time?",
      answer: "The policy lapses, and you may lose your NCB or face inspections for renewal.",
    },
    {
      question: "Is there an NCB benefit for electric scooters?",
      answer: "Yes, No Claim Bonus applies regardless of fuel type.",
    },
    {
      question: "What’s the ideal IDV for OLA scooters?",
      answer: "IDV should reflect the current market value of your model. Too low reduces payout, too high increases premium.",
    },
    {
      question: "Does Zero Depreciation really help?",
      answer: "Yes, especially during the first few years when parts replacement is costly.",
    },
    {
      question: "How do I claim insurance for accident damage?",
      answer: "Register the claim, visit an authorized garage, and submit required documents as per insurer instructions.",
    },
    {
      question: "What is Roadside Assistance and is it needed for OLA?",
      answer: "It covers emergencies like towing, flat battery, or breakdown. Highly recommended for electric scooters.",
    },
    {
      question: "Are electric scooters more expensive to insure?",
      answer: "Not always. Many EVs have lower premiums due to fewer moving parts, but repair costs can be higher.",
    },
    {
      question: "Can I transfer my policy if I sell my OLA?",
      answer: "Yes, the insurance can be transferred to the new owner. NCB, however, stays with you.",
    },
    {
      question: "What’s Return to Invoice cover?",
      answer: "It reimburses the full invoice value (including taxes and registration) in case of total loss or theft.",
    },
    {
      question: "Does insurance cover OTA software failures?",
      answer: "Typically no. Software issues are handled under manufacturer warranty, not insurance.",
    },
    {
      question: "What if I modify my scooter with accessories?",
      answer: "Declare all modifications to ensure they’re covered during claims.",
    },
    {
      question: "Can I renew insurance online?",
      answer: "Yes, TheFinMaster offers online renewals with instant policy documents.",
    },
    {
      question: "Is coverage available in Tier-2 or Tier-3 towns?",
      answer: "Yes, most insurers now have a pan-India reach including smaller towns.",
    },
    {
      question: "What if my OLA is stolen?",
      answer: "File an FIR, inform your insurer, and follow the claim process. If covered, you'll receive the IDV or invoice value (if add-on taken).",
    },
  ];

  const toggleAccordion = (index) => {
    setActiveIndex(activeIndex === index ? null : index);
  };

  const cardsNew = [
    {
      title: "Battery Protection Cover",
      description: "Protects against battery damage or replacement costs.",
      icon: <FaTools className="text-4xl text-yellow-600 mb-4" />,
    },
    {
      title: "Zero Depreciation Cover",
      description: "Ensures full claim without deductions on parts.",
      icon: <FaShieldAlt className="text-4xl text-red-500 mb-4" />,
    },
    {
      title: "Roadside Assistance",
      description: "Covers towing, flat battery, or breakdowns.",
      icon: <FaTruckPickup className="text-4xl text-blue-500 mb-4" />,
    },
    {
      title: "Charger & Wiring Cover",
      description: "Secures home charger and EV wiring from theft or damage.",
      icon: <FaFileInvoice className="text-4xl text-purple-600 mb-4" />,
    },
    {
      title: "Return to Invoice",
      description: "Helps recover the full invoice value in case of total loss.",
      icon: <FaFileInvoice className="text-4xl text-purple-600 mb-4" />,
    },
    {
      title: "Consumables Cover",
      description: "Adds protection for nuts, bolts, brake oil, etc.",
      icon: <FaOilCan className="text-4xl text-green-600 mb-4" />,
    },
  ];

  const features = [
    { icon: '🛡️', text: 'Reliable & Accurate Estimates' },
    { icon: '⏱️', text: 'Takes Less Than a Minute' },
    { icon: '📉', text: 'Instantly Shows Premium Breakup' },
    { icon: '📄', text: 'No Paperwork or Documentation' },
    { icon: '📲', text: 'Easy to Use on Any Device' },
    { icon: '💰', text: 'Helps You Save on Insurance' },
  ];

  const benefits = [
    {
      title: "OLA S1 X / S1 X+",
      desc: "Entry-level electric scooters for budget-conscious users.",
      icon: <Rocket className="w-7 h-7 text-red-500" />,
      gradient: "from-red-50 to-red-100",
    },
    {
      title: "OLA S1 Air",
      desc: "Lightweight, value-for-money EV for everyday riders.",
      icon: <ShieldCheck className="w-7 h-7 text-orange-500" />,
      gradient: "from-orange-50 to-orange-100",
    },
    {
      title: "OLA S1 Pro (Gen 2)",
      desc: "High-performance EV with long range and smart features.",
      icon: <Bike className="w-7 h-7 text-indigo-500" />,
      gradient: "from-indigo-50 to-indigo-100",
    },
    {
      title: "OLA S1",
      desc: "The original flagship with balanced performance and price.",
      icon: <GaugeCircle className="w-7 h-7 text-cyan-500" />,
      gradient: "from-cyan-50 to-cyan-100",
    },
  ];
const olainsurance = [
    {
      name: "S1X",
      path: "/ola-s1x-bike-insurance", 
      image: "/assets/olas1xplus.jpeg",
    },
  {
      name: "S1X Plus",
      path: "/ola-s1x-plus-bike-insurance", 
      image: "/assets/olas1x.jpeg",
    },
    {
      name: "S1 Pro",
      path: "/ola-s1pro-bike-insurance", 
      image: "/assets/olas1pro.jpeg",
    },
    {
      name: "S1 Air",
      path: "/ola-s1-air-bike-insurance", 
      image: "/assets/olas1air.jpeg",
    },
    {
      name: "Roadster X",
      path: "/ola-roadsterx-bike-insurance", 
      image: "/assets/olaroadsterx.jpeg",
    },
    
     {
      name: "S1 Pro Sport",
      path: "/ola-s1-pro-sport-bike-insurance", 
      image: "/assets/olas1prosport.jpeg",
    },
  ];
  return (
    <>
      <Helmet>
        <title>OLA Electric Scooter Insurance in India – Compare Quotes & Buy Online</title>
        <meta
          name="description"
          content="Explore OLA electric scooter insurance options in India. Compare quotes, customize coverage, and buy online to protect your OLA scooter."
        />
        <meta
          name="keywords"
          content="ola electric scooter insurance, ev scooter insurance india, ola s1 pro insurance, ola s1 air insurance, two-wheeler insurance quotes"
        />
         <link rel="canonical" href="https://thefinmaster.com/ola-electric-two-wheeler-insurance" />
        <meta
          name="robots"
          content="max-snippet:-1, max-image-preview:standard"
        />
        <meta name="robots" content="max-video-preview:-1" />
        {/* <link rel="canonical" href="https://thefinmaster.com/ola-electric-scooter-insurance" />
        <meta name="robots" content="max-snippet:-1, max-image-preview:standard, max-video-preview:-1" />
        <meta property="og:title" content="OLA Electric Scooter Insurance in India" />
        <meta property="og:description" content="Compare and buy OLA electric scooter insurance online with ease." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://thefinmaster.com/ola-electric-scooter-insurance" />
        <meta name="twitter:card" content="summary_large_image" /> */}
      </Helmet>
      <FAQSchema faqs={faqs} />
      <div className="w-full flex justify-center items-center px-4 mt-20">
        <div className="w-full max-w-5xl h-auto object-contain rounded-lg shadow-md transition duration-300 hover:scale-105">
          <AdBanner displayOrder="1" />
        </div>
      </div>
      <nav className="text-gray-500 text-sm my-4 ml-10" aria-label="Breadcrumb">
        <ol className="list-none p-0 inline-flex">
          <li className="flex items-center">
            <Link to="/" className="text-blue-600 hover:text-blue-800">Home</Link>
            <svg
              className="w-4 h-4 mx-2 text-gray-500"
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path
                fillRule="evenodd"
                d="M7.293 14.707a1 1 0 010-1.414L11.586 9 7.293 4.707a1 1 0 111.414-1.414l5 5a1 1 0 010 1.414l-5 5a1 1 0 01-1.414 0z"
                clipRule="evenodd"
              />
            </svg>
          </li>
          <li className="flex items-center">
            <span className="text-gray-500">OLA Electric Scooter Insurance</span>
          </li>
        </ol>
      </nav>
      <div className="w-full flex flex-col items-center">
        <div className="w-full flex justify-center">
          <div className="w-full sm:w-[95%] flex flex-col sm:flex-row justify-center items-center">
            <div className="w-full sm:w-[50%]">
              <div className="w-[90%]">
                <h1 className="text-3xl md:text-5xl font-bold mt-6 md:mt-0 md:pl-4 pl-6">
                  <span className="text-blue-600">OLA Electric Scooter Insurance </span> 
                </h1>
                <p className="text-lg md:text-base lg:text-lg mt-4 pl-4">
                Ola Electric has made a strong mark in the EV market with its stylish design, advanced technology, and impressive performance. Whether you ride the Ola S1 Air or the Ola S1 Pro, keeping your scooter protected starts with the right insurance plan. This guide walks you through everything you need to know about insuring an Ola electric scooter, from average premiums and model insights to useful add-ons, renewal tips, and FAQs.
                </p>
              </div>
            </div>
            <div className="w-full sm:w-[50%]">
              <img
                className="w-full max-w-full mt-2"
                src="../assets/electricinsurance.jpeg"
                alt="OLA Electric Scooter"
              />
            </div>
          </div>
        </div>
      </div>
      <section className="bg-gradient-to-r from-blue-50 via-white to-blue-100 py-16 px-6 sm:px-12 lg:px-20 rounded-3xl shadow-lg max-w-7xl mx-auto mt-12">
        <div className="text-center mb-12">
          <h2 className="text-3xl font-bold text-slate-800">
            About OLA Electric in India
          </h2>
          <p className="text-slate-600 mt-3 max-w-2xl mx-auto">
            OLA Electric, an offshoot of the ride-hailing giant OLA, entered the electric two-wheeler market in 2021. Since then, it has launched multiple models aimed at different budgets while offering smart features like touchscreen dashboards, app connectivity, and impressive battery ranges.
          </p>
        </div>
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-2">
          {benefits.map((item, index) => (
            <div
              key={index}
              className={`bg-gradient-to-br ${item.gradient} rounded-2xl p-5 shadow hover:shadow-md hover:scale-[1.02] transition-transform`}
            >
              <div className="flex items-center justify-center bg-white w-14 h-14 rounded-full shadow mb-4 mx-auto">
                {item.icon}
              </div>
              <h3 className="text-lg font-semibold text-slate-800 text-center mb-2">
                {item.title}
              </h3>
              <p className="text-sm text-slate-600 text-center leading-relaxed">
                {item.desc}
              </p>
            </div>
          ))}
        </div>
      </section>

       <div className="p-4 sm:p-8 bg-white">
              <h2 className="text-xl sm:text-2xl font-bold mb-6 text-center">
                Choose Your Bike Brand
              </h2>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
                {olainsurance.map((calc, index) => (
                  <motion.div
                    key={index}
                    whileHover={{ scale: 1.04 }}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.3, delay: index * 0.05 }}
                  >
                    <Link
                      to={calc.path}
                      className="flex flex-col items-center justify-center text-center border border-gray-200 rounded-lg p-5 bg-white shadow-sm hover:shadow-md transition-all duration-300 h-full min-h-[160px]"
                    >
                      <img
                        src={calc.image}
                        alt={calc.name}
                        className="w-16 h-16 object-contain mb-2"
                      />
                      <span className="text-md font-medium text-gray-800 break-words leading-snug">
                        {calc.name}
                      </span>
                    </Link>
                  </motion.div>
                ))}
              </div>
            </div>
      <div className="w-full flex justify-center items-center my-6 px-4">
        <div className="w-full max-w-[984px]">
          <AdBanner displayOrder="2" />
        </div>
      </div>
      <section className="relative py-20 px-4 sm:px-8 bg-gradient-to-br from-white to-gray-100 overflow-hidden">
        <div className="absolute inset-0 z-0 pointer-events-none">
          <div className="absolute top-[-10%] left-[-10%] w-96 h-96 bg-blue-400 opacity-20 blur-3xl rounded-full"></div>
          <div className="absolute bottom-[-10%] right-[-10%] w-96 h-96 bg-purple-400 opacity-20 blur-3xl rounded-full"></div>
        </div>
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7 }}
          viewport={{ once: true }}
          className="relative z-10 max-w-5xl mx-auto bg-white/90 backdrop-blur-lg rounded-2xl shadow-xl p-10"
        >
          <h2 className="text-3xl md:text-4xl font-bold text-gray-800 mb-6">
            Why OLA Scooter Owners Need Insurance
          </h2>
          <p className="text-gray-700 text-lg leading-relaxed mb-6">
            Electric scooters come with advanced tech and lithium-ion battery systems, making them expensive to repair in case of accidents or electrical failures. Insurance not only fulfills legal requirements but also helps you stay financially secure. Here’s why it’s crucial:
          </p>
          <ul className="grid gap-4 sm:grid-cols-2 md:grid-cols-3 text-gray-700 mb-6">
            <li className="flex items-start gap-3">
              <FaCarCrash className="text-xl text-red-500 mt-1" />
              <span>Covers accidental damages, theft, fire, and natural calamities</span>
            </li>
            <li className="flex items-start gap-3">
              <FaCloudSunRain className="text-xl text-blue-500 mt-1" />
              <span>Mandatory under Indian Motor Vehicles Act</span>
            </li>
            <li className="flex items-start gap-3">
              <FaUserLock className="text-xl text-yellow-500 mt-1" />
              <span>Special add-ons for battery and charger protection</span>
            </li>
            <li className="flex items-start gap-3">
              <FaShieldAlt className="text-xl text-green-500 mt-1" />
              <span>Offers peace of mind in case of unexpected breakdowns</span>
            </li>
            <li className="flex items-start gap-3">
              <FaUserShield className="text-xl text-indigo-500 mt-1" />
              <span>Enables cashless servicing at authorized OLA facilities</span>
            </li>
          </ul>
          <div className="bg-indigo-50 border-l-4 border-indigo-400 p-4 rounded-lg text-indigo-700">
            A comprehensive policy ensures your OLA stays protected, legally compliant, and ready for the road.
          </div>
        </motion.div>
      </section>
       <section className="bg-blue-50 py-6 px-4 sm:px-6 md:px-20 lg:px-20 rounded-3xl shadow-lg max-w-6xl mx-auto mt-10">
          <div className="flex flex-col md:flex-row justify-between items-center gap-10">
            <div className="max-w-xl space-y-6 text-center md:text-left">
              <h2 className="text-2xl sm:text-3xl font-bold text-blue-900 leading-tight">
                Calculate Your TwoWheeler Insurance <br />
                in <span className="text-blue-600">Seconds</span>
              </h2>
              <p className="text-gray-700 text-lg">
                Use our fast and free calculator to estimate your premium — no paperwork, no waiting.
              </p>
              <Link
                to="/financial-tools-calculators/bike-insurance-calculator"
                className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 text-lg rounded-full shadow-md transition"
              >
                Start Calculating <ArrowRight className="w-5 h-5" />
              </Link>
            </div>
            <div className="space-y-4 text-left">
              {features.map((item, index) => (
                <div key={index} className="flex items-center gap-3">
                  <span className="text-xl" role="img" aria-label={item.text}>{item.icon}</span>
                  <span className="text-gray-800 font-medium">{item.text}</span>
                </div>
              ))}
            </div>
          </div>
        </section>
      <div className="container mx-auto p-6 mt-6">
        <h2 className="text-2xl md:text-3xl font-semibold text-gray-800 text-center">
          Popular OLA Models and Estimated Insurance Premiums
        </h2>
        <p className="text-gray-600 text-sm md:text-base mt-2 text-center">
          Below are the estimated insured declared values (IDV) and starting insurance premiums for key OLA models:
        </p>
        <div className="max-w-7xl mx-auto px-4 py-6">
          <div className="block md:hidden">
            {[
              { model: "OLA S1 X / X+", category: "Budget EV", idv: "80,000 – 1.00 Lakh", premium: "₹1,000 – ₹1,300" },
              { model: "OLA S1 Air", category: "Mid-range EV", idv: "1.10 – 1.25 Lakh", premium: "₹1,200 – ₹1,500" },
              { model: "OLA S1 Pro (Gen 2)", category: "Performance EV", idv: "1.40 – 1.60 Lakh", premium: "₹1,400 – ₹1,800" },
              { model: "OLA S1 (Gen 1)", category: "Original Flagship", idv: "1.20 – 1.35 Lakh", premium: "₹1,300 – ₹1,600" },
            ].map((item, index) => (
              <div key={index} className="border mb-4 p-4 rounded-lg bg-white shadow-md">
                <h3 className="font-semibold text-gray-800 mb-2">{item.model}</h3>
                <div className="text-gray-600 mb-1"><strong>Category:</strong> {item.category}</div>
                <div className="text-gray-600 mb-1"><strong>Approx. IDV:</strong> {item.idv}</div>
                <div className="text-gray-600"><strong>Starting Premium:</strong> {item.premium}</div>
              </div>
            ))}
          </div>
          <div className="hidden md:block overflow-x-auto">
            <table className="min-w-full border-collapse border border-gray-300">
              <thead className="bg-gray-100 text-gray-800">
                <tr>
                  <th className="p-3 border border-gray-300 text-left">OLA Model</th>
                  <th className="p-3 border border-gray-300 text-left">Type</th>
                  <th className="p-3 border border-gray-300 text-left">Approx. IDV (₹)</th>
                  <th className="p-3 border border-gray-300 text-left">Starting Premium (₹)</th>
                </tr>
              </thead>
              <tbody className="text-gray-700">
                {[
                  ["OLA S1 X / X+", "Budget EV", "80,000 – 1.00 Lakh", "₹1,000 – ₹1,300"],
                  ["OLA S1 Air", "Mid-range EV", "1.10 – 1.25 Lakh", "₹1,200 – ₹1,500"],
                  ["OLA S1 Pro (Gen 2)", "Performance EV", "1.40 – 1.60 Lakh", "₹1,400 – ₹1,800"],
                  ["OLA S1 (Gen 1)", "Original Flagship", "1.20 – 1.35 Lakh", "₹1,300 – ₹1,600"],
                ].map(([model, category, idv, premium], index) => (
                  <tr key={index} className="border border-gray-300">
                    <td className="p-3 border border-gray-300">{model}</td>
                    <td className="p-3 border border-gray-300">{category}</td>
                    <td className="p-3 border border-gray-300">{idv}</td>
                    <td className="p-3 border border-gray-300">{premium}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
      <div className="container mx-auto p-6 mt-2">
        <h2 className="text-2xl md:text-3xl font-semibold text-gray-800 text-center">
          Recommended Add-Ons for OLA Insurance
        </h2>
        <p className="text-gray-600 text-sm md:text-base mt-2 text-center">
          Given the EV-specific features and hardware, OLA owners should consider these add-ons:
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-3 gap-6 mt-4">
          {cardsNew.map((card, index) => (
            <div
              key={index}
              className="border rounded-lg shadow-lg p-6 flex flex-col items-center text-center"
            >
              {card.icon}
              <h3 className="text-lg font-semibold mb-2">{card.title}</h3>
              <p className="text-gray-600">{card.description}</p>
            </div>
          ))}
        </div>
      </div>
      <div className="w-full flex justify-center items-center my-6 px-4">
        <div className="w-full max-w-[984px]">
          <AdBanner displayOrder="3" />
        </div>
      </div>
      <div className="mx-auto container p-6 mt-6">
        <h2 className="text-2xl md:text-3xl font-semibold text-gray-800 text-center">
          Best Insurance Providers for OLA Scooters
        </h2>
        <p className="text-gray-600 text-sm md:text-base mt-2 text-center">
          Insurers with experience in electric vehicles offer better coverage and support:
        </p>
        <div className="mt-6 grid md:grid-cols-3 gap-6 text-left">
          <div className="p-6 border rounded-lg shadow-md bg-white">
            <span className="text-3xl">📱</span>
            <h3 className="text-lg font-semibold text-gray-800 mt-2">ACKO</h3>
            <p className="text-gray-700 text-sm mt-1">Paperless claims, EV-focused offerings.</p>
          </div>
          <div className="p-6 border rounded-lg shadow-md bg-white">
            <span className="text-3xl">⚡</span>
            <h3 className="text-lg font-semibold text-gray-800 mt-2">Bajaj Allianz</h3>
            <p className="text-gray-700 text-sm mt-1">Known for customized EV add-ons.</p>
          </div>
          <div className="p-6 border rounded-lg shadow-md bg-white">
            <span className="text-3xl">💰</span>
            <h3 className="text-lg font-semibold text-gray-800 mt-2">TATA AIG</h3>
            <p className="text-gray-700 text-sm mt-1">Offers battery and wiring-specific covers.</p>
          </div>
          <div className="p-6 border rounded-lg shadow-md bg-white">
            <span className="text-3xl">🚗</span>
            <h3 className="text-lg font-semibold text-gray-800 mt-2">Digit Insurance</h3>
            <p className="text-gray-700 text-sm mt-1">App-based process with good urban coverage.</p>
          </div>
          <div className="p-6 border rounded-lg shadow-md bg-white">
            <span className="text-3xl">🏥</span>
            <h3 className="text-lg font-semibold text-gray-800 mt-2">ICICI Lombard</h3>
            <p className="text-gray-700 text-sm mt-1">Robust service network and flexible plans.</p>
          </div>
        </div>
      </div>
      <div className="w-full px-2 py-6 bg-gradient-to-br from-sky-50 via-white to-sky-100 dark:from-gray-900 dark:to-gray-800">
        <div className="max-w-6xl mx-auto grid gap-10">
          <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-lg p-6 md:p-10 border border-sky-200 dark:border-gray-700 hover:shadow-xl transition-shadow">
            <div className="flex items-center gap-3 mb-2">
              <FileText className="w-8 h-8 text-sky-600" />
              <span className="text-2xl md:text-3xl font-bold text-gray-800 dark:text-white">
                Tips for Getting the Right Insurance
              </span>
            </div>
            <h3 className="text-xl md:text-xl font-bold text-gray-800 dark:text-white mb-6">
              To get the best premium rates and coverage benefits, follow these smart tips:
            </h3>
            <div className="grid sm:grid-cols-2 gap-6 text-gray-700 dark:text-gray-300">
              <div className="p-5 bg-sky-50 dark:bg-gray-800 rounded-xl">
                <span>Always opt for comprehensive coverage for EVs.</span>
              </div>
              <div className="p-5 bg-sky-50 dark:bg-gray-800 rounded-xl">
                <span>Choose the right IDV (Insured Declared Value) based on current model.</span>
              </div>
              <div className="p-5 bg-sky-50 dark:bg-gray-800 rounded-xl">
                <span>Don’t skip the battery and charger add-ons.</span>
              </div>
              <div className="p-5 bg-sky-50 dark:bg-gray-800 rounded-xl">
                <span>Renew before expiry to retain your No Claim Bonus.</span>
              </div>
              <div className="p-5 bg-sky-50 dark:bg-gray-800 rounded-xl">
                <span>Use TheFinMaster’s quote comparison to get the best deal.</span>
              </div>
            </div>
          </div>
          <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-lg p-6 md:p-10 border border-emerald-200 dark:border-gray-700 hover:shadow-xl transition-shadow">
            <div className="flex items-center gap-3 mb-6">
              <BadgeCheck className="w-8 h-8 text-emerald-600" />
              <span className="text-2xl md:text-3xl font-bold text-gray-800 dark:text-white">
                Documents Required for OLA Scooter Insurance
              </span>
            </div>
            <h3 className="text-xl md:text-xl font-bold text-gray-800 dark:text-white mb-6">
              Keep these documents ready:
            </h3>
            <div className="grid sm:grid-cols-2 gap-6 text-gray-700 dark:text-gray-300">
              <div className="p-5 bg-emerald-50 dark:bg-gray-800 rounded-xl">
                <span>RC (Registration Certificate)</span>
              </div>
              <div className="p-5 bg-emerald-50 dark:bg-gray-800 rounded-xl">
                <span>Previous policy copy (if applicable)</span>
              </div>
              <div className="p-5 bg-emerald-50 dark:bg-gray-800 rounded-xl">
                <span>Identity proof (Aadhaar/PAN)</span>
              </div>
              <div className="p-5 bg-emerald-50 dark:bg-gray-800 rounded-xl">
                <span>Invoice of the scooter (for new policies)</span>
              </div>
              <div className="p-5 bg-emerald-50 dark:bg-gray-800 rounded-xl sm:col-span-2">
                <span>PUC certificate (for hybrid or dual-fuel vehicles)</span>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="max-w-4xl mx-auto py-6 px-2">
        <h2 className="text-2xl md:text-3xl font-semibold text-center mb-8">
          FAQs About OLA Scooter Insurance
        </h2>
        <div className="space-y-4">
          {faqs.map((faq, index) => (
            <div
              key={index}
              className="border rounded-lg overflow-hidden shadow-md"
            >
              <button
                onClick={() => toggleAccordion(index)}
                className="w-full p-4 text-left text-lg font-semibold focus:outline-none flex justify-between items-center"
                aria-expanded={activeIndex === index}
                aria-controls={`faq-answer-${index}`}
              >
                {faq.question}
                <Icon id={index} open={activeIndex} />
              </button>
              {activeIndex === index && (
                <div id={`faq-answer-${index}`} className="p-4 bg-gray-100">
                  <p className="text-gray-700">{faq.answer}</p>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
      <section className="relative py-20 px-4 sm:px-8 bg-gradient-to-br from-white to-gray-100 overflow-hidden">
        <div className="absolute inset-0 pointer-events-none z-0">
          <div className="absolute top-[-20%] left-[-10%] w-96 h-96 bg-indigo-300 opacity-30 blur-3xl rounded-full"></div>
          <div className="absolute bottom-[-10%] right-[-10%] w-96 h-96 bg-purple-300 opacity-30 blur-3xl rounded-full"></div>
        </div>
        <motion.div
          initial={{ opacity: 0, y: 60 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7 }}
          viewport={{ once: true }}
          className="relative z-10 max-w-4xl mx-auto bg-white/90 backdrop-blur-sm border border-gray-200 shadow-xl rounded-2xl p-10"
        >
          <div className="flex flex-col md:flex-row items-start md:items-center gap-6">
            <div className="text-indigo-600 text-5xl">
              <GiScooter />
            </div>
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-800 mb-4">
                Conclusion
              </h2>
              <p className="text-gray-700 text-lg leading-relaxed mb-4">
                OLA scooters are fast becoming a go-to option for urban Indians seeking affordable, tech-savvy, and green mobility. With smart dashboards, powerful motors, and fast charging, they are a glimpse into the future — but even the smartest scooters need robust protection.
              </p>
              <p className="text-gray-700 text-lg leading-relaxed">
                At TheFinMaster, you can explore and compare insurance plans tailored for OLA scooters. Choose from leading insurers, customize your coverage, and ride worry-free — today and every day.
              </p>
            </div>
          </div>
        </motion.div>
      </section>
    </>
  );
};

export default OlaElectricScooterInsurance;